import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Search, Send, User, Upload, X, Check } from 'lucide-react';
import { api } from '../api/apis';

const StreamerChallenge = () => {
    const [streamers, setStreamers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);

    // Multi-select streamers
    const [selectedStreamers, setSelectedStreamers] = useState([]);

    // Card Upload State
    const [cardFile, setCardFile] = useState(null);
    const [cardPreview, setCardPreview] = useState(null);
    const [cardTitle, setCardTitle] = useState('');
    const [cardRarity, setCardRarity] = useState('Common');
    const [cardPrice, setCardPrice] = useState(0);

    const [streamerSearch, setStreamerSearch] = useState('');

    useEffect(() => {
        fetchStreamers();
    }, []);

    const fetchStreamers = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const res = await api.get('/auth/user/getStreamers', config);

            // The backend returns { streamers: [...] } or just [...]
            const list = res.data.streamers || res.data || [];
            setStreamers(list);
        } catch (error) {
            console.error('Error fetching streamers:', error);
            toast.error('Failed to load streamers');
        } finally {
            setLoading(false);
        }
    };

    const handleStreamerToggle = (streamer) => {
        if (selectedStreamers.find(s => s._id === streamer._id)) {
            setSelectedStreamers(selectedStreamers.filter(s => s._id !== streamer._id));
        } else {
            setSelectedStreamers([...selectedStreamers, streamer]);
        }
    };

    const handleSelectAll = () => {
        if (selectedStreamers.length === filteredStreamers.length) {
            setSelectedStreamers([]);
        } else {
            setSelectedStreamers(filteredStreamers);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCardFile(file);
            setCardPreview(URL.createObjectURL(file));
        }
    };

    const handleSendChallenge = async () => {
        if (selectedStreamers.length === 0) {
            toast.error('Please select at least one streamer');
            return;
        }
        if (!cardFile) {
            toast.error('Please upload a card image');
            return;
        }

        setSending(true);
        try {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                toast.error('Please log in as admin first');
                setSending(false);
                return;
            }
            const config = { headers: { Authorization: `Bearer ${token}` } };

            // 1. Upload Image
            const formData = new FormData();
            formData.append('image', cardFile);

            const uploadRes = await api.post('/auth/admin/upload-broadcast-card-image', formData, {
                headers: {
                    ...config.headers,
                    'Content-Type': 'multipart/form-data'
                }
            });

            const imageUrl = uploadRes.data.imageUrl;
            if (!imageUrl) throw new Error('Image upload failed');

            // 2. Create Card (use defaults for title and price)
            // Must match Mongoose schema requirements
            const cardData = {
                cardTitle: 'CREZAR Challenge',
                description: 'Admin Challenge',
                imageUrl,
                rarity: cardRarity, // Already Title Case
                price: 0,
                category: ['Challenge'], // Must be an array
                streamerEmailId: 'admin@crezar.com', // Required by schema
                userEmailId: 'admin@crezar.com', // Required by schema
                userName: 'Admin',
                creatorName: 'Admin',
                duration: 60
            };

            const createCardRes = await api.post('/auth/admin/cards', cardData, config);
            const newCardId = createCardRes.data._id;

            if (!newCardId) throw new Error('Card creation failed');

            // 3. Send Challenge to each streamer
            let successCount = 0;
            for (const streamer of selectedStreamers) {
                try {
                    await api.post('/auth/admin/admin/challenges/send', {
                        streamerUsername: streamer.username,
                        cardId: newCardId,
                        message: ''
                    }, config);
                    successCount++;
                } catch (err) {
                    console.error(`Failed to send to ${streamer.username}`, err);
                }
            }

            toast.success(`Challenge sent to ${successCount} streamers!`);

            // Reset form
            setSelectedStreamers([]);
            setCardFile(null);
            setCardPreview(null);

        } catch (error) {
            console.error('Error sending challenge:', error);
            toast.error(error.response?.data?.error || error.message || 'Failed to send challenge');
        } finally {
            setSending(false);
        }
    };

    const filteredStreamers = streamers.filter(s =>
        (s.username || '').toLowerCase().includes(streamerSearch.toLowerCase()) ||
        (s.emailId || '').toLowerCase().includes(streamerSearch.toLowerCase())
    );

    return (
        <div className="p-6 space-y-8 bg-gray-900 min-h-screen text-white">
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
                    Streamer Challenges
                </h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Streamer Selection Panel */}
                <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 h-[600px] flex flex-col">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold flex items-center gap-2">
                            <User className="text-yellow-400" /> Select Streamers
                            <span className="text-sm font-normal text-gray-400 ml-2">
                                ({selectedStreamers.length} selected)
                            </span>
                        </h2>
                        <button
                            onClick={handleSelectAll}
                            className="text-sm text-yellow-400 hover:text-yellow-300"
                        >
                            {selectedStreamers.length === filteredStreamers.length ? 'Deselect All' : 'Select All'}
                        </button>
                    </div>

                    <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search streamers..."
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white focus:border-yellow-500 focus:outline-none"
                            value={streamerSearch}
                            onChange={(e) => setStreamerSearch(e.target.value)}
                        />
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-2 custom-scrollbar pr-2">
                        {filteredStreamers.length === 0 ? (
                            <div className="text-center text-gray-500 mt-10">No streamers found</div>
                        ) : (
                            filteredStreamers.map(streamer => {
                                const isSelected = selectedStreamers.some(s => s._id === streamer._id);
                                return (
                                    <div
                                        key={streamer._id}
                                        onClick={() => handleStreamerToggle(streamer)}
                                        className={`p-3 rounded-lg cursor-pointer transition-all flex items-center gap-3 border ${isSelected
                                            ? 'bg-yellow-500/20 border-yellow-500'
                                            : 'bg-gray-900/50 hover:bg-gray-700 border-transparent'
                                            }`}
                                    >
                                        <div className={`w-5 h-5 rounded border flex items-center justify-center ${isSelected ? 'bg-yellow-500 border-yellow-500' : 'border-gray-500'
                                            }`}>
                                            {isSelected && <Check className="w-3 h-3 text-black" />}
                                        </div>

                                        <div className="w-10 h-10 rounded-full bg-gray-700 overflow-hidden">
                                            {streamer.profilePhoto ? (
                                                <img src={streamer.profilePhoto} alt={streamer.username} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-lg font-bold">
                                                    {(streamer.username || '?')[0].toUpperCase()}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium">{streamer.username}</p>
                                            <p className="text-xs text-gray-400">{streamer.emailId}</p>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Card Upload Panel */}
                <div className="space-y-6">
                    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                            <Upload className="text-yellow-400" /> Upload Challenge Card
                        </h2>

                        <div className="space-y-4">
                            {/* Image Upload */}
                            <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 text-center hover:border-yellow-500 transition-colors relative">
                                {cardPreview ? (
                                    <div className="relative inline-block">
                                        <img src={cardPreview} alt="Preview" className="h-48 object-contain rounded" />
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setCardFile(null);
                                                setCardPreview(null);
                                            }}
                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-lg hover:bg-red-600"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <label className="cursor-pointer block py-8">
                                        <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                                        <span className="text-gray-400">Click to upload card image</span>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={handleImageChange}
                                        />
                                    </label>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">Rarity</label>
                                    <select
                                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-yellow-500 focus:outline-none"
                                        value={cardRarity}
                                        onChange={(e) => setCardRarity(e.target.value)}
                                    >
                                        <option value="Common">Common</option>
                                        <option value="Rare">Rare</option>
                                        <option value="Epic">Epic</option>
                                        <option value="Legendary">Legendary</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Send Button */}
                    <button
                        onClick={handleSendChallenge}
                        disabled={sending || selectedStreamers.length === 0 || !cardFile}
                        className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all ${sending || selectedStreamers.length === 0 || !cardFile
                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black hover:from-yellow-500 hover:to-yellow-700 shadow-lg shadow-yellow-500/20'
                            }`}
                    >
                        {sending ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                                Sending to {selectedStreamers.length} Streamers...
                            </>
                        ) : (
                            <>
                                <Send className="w-5 h-5" />
                                Send Challenge ({selectedStreamers.length})
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StreamerChallenge;
